import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { authService, getSamlLoginUrl } from "../../services/api";
import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import { Mail, Lock, Eye, EyeOff, ChevronLeft, ChevronRight } from "lucide-react";
import toast from "react-hot-toast";
import { FcGoogle } from "react-icons/fc";
import { FaFacebookF, FaTwitter, FaLinkedinIn, FaInstagram } from "react-icons/fa";
import React from "react";

// Carousel slides with images and content
const carouselSlides = [
  {
    image: "/image1.jpg",
    title: "Empowering Minds",
    subtitle: "Transform your learning experience with AI-powered insights and personalized education paths."
  },
  {
    image: "/image2.jpg",
    title: "Learn Without Limits",
    subtitle: "Access world-class courses anytime, anywhere. Your classroom is wherever you are."
  },
  {
    image: "/image3.jpg",
    title: "Track Your Progress",
    subtitle: "Real-time analytics and smart assessments to help you achieve your academic goals."
  },
  {
    image: "/image4.jpg",
    title: "Collaborate & Grow",
    subtitle: "Connect with peers and mentors in an interactive learning community."
  },
  {
    image: "/image5.jpg",
    title: "Future of Education",
    subtitle: "Experience next-generation learning tools designed for tomorrow's leaders."
  }
];

const Login = () => {
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [samlLoading, setSamlLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);
  

  const navigate = useNavigate();
  const { login } = useAuth();

  
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % carouselSlides.length);
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  const nextSlide = () => {
    setCurrentImageIndex((prev) => (prev + 1) % carouselSlides.length);
  };

  const prevSlide = () => {
    setCurrentImageIndex((prev) =>
      prev === 0 ? carouselSlides.length - 1 : prev - 1
    );
  };

  // Preload images
  useEffect(() => {
    carouselSlides.forEach((slide) => {
      const img = new Image();
      img.src = slide.image;
    });
  }, []);

  // Auto-advance carousel
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => {
        const nextIndex = (prevIndex + 1) % carouselSlides.length;
        return nextIndex;
      });
    }, 10000);

    return () => clearInterval(interval);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email || !formData.password) {
      toast.error("Please fill in all fields");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const response = await authService.login(
        formData.email,
        formData.password
      );
      
      if (response.data) {
        // Pass both token and refreshToken to login context
        login({
          user: response.data.user,
          token: response.data.token || response.data.accessToken,
          refreshToken: response.data.refreshToken
        });
        let redirectPath = "";
        if (response.data.user.role === "admin") {
          redirectPath = "/admin";
        } else {
          redirectPath =
            response.data.user.role === "teacher"
              ? "/teacher/dashboard"
              : "/student/dashboard";
        }
        toast.success("Login successful!");
        navigate(redirectPath);
      }
    } catch (err) {
      console.log("error", err);
      toast.error("Login failed. Please check your credentials and try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    try {
      setSamlLoading(true);
      // Backend initiates SAML flow and redirects back to /auth/saml/callback or /auth/saml/failure
      window.location.assign(getSamlLoginUrl());
    } catch (e) {
      console.error(e);
      setSamlLoading(false);
      toast.error("Unable to start Google login. Please try again.");
    }
  };

    React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
  <div className="h-screen w-screen flex items-center justify-center relative overflow-hidden font-sans bg-[#eef6fb]">

    {/* 🔥 RESPONSIVE MODERN BACKGROUND */}
    <div className="absolute inset-0 z-0 overflow-">

      {isMobile ? (
        <>
          <div
            className="absolute inset-0"
            style={{
              background: `
                radial-gradient(circle at 15% 25%, rgba(255,255,255,0.55), transparent 40%),
                radial-gradient(circle at 85% 15%, rgba(91,182,230,0.45), transparent 45%),
                radial-gradient(circle at 40% 60%, rgba(66,156,205,0.35), transparent 55%),
                linear-gradient(135deg, #5bb6e6 0%, #429ccd 60%, #3aa0d8 100%)
              `,
              clipPath: "polygon(0 0, 100% 0, 100% 55%, 0 35%)"
            }}
          />

          <div
            className="absolute inset-0"
            style={{
              background: `
                radial-gradient(circle at 25% 75%, rgba(26,66,95,0.55), transparent 45%),
                radial-gradient(circle at 80% 85%, rgba(16,42,67,0.55), transparent 55%),
                radial-gradient(circle at 60% 50%, rgba(20,55,80,0.35), transparent 60%),
                linear-gradient(135deg, #1a425f 0%, #163a55 55%, #102a43 100%)
              `,
              clipPath: "polygon(0 35%, 100% 55%, 100% 100%, 0 100%)"
            }}
          />
        </>
      ) : (
        <>
          <div
            className="absolute inset-0"
            style={{
              background: "linear-gradient(135deg, #eef6fb, #ffffff)",
              clipPath: "polygon(65% 0, 100% 0, 100% 100%, 82% 100%)"
            }}
          />

          {carouselSlides.map((slide, index) => (
            <div key={index} className="absolute inset-0">
              <img
                src={slide.image}
                className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
                  index === currentImageIndex ? "opacity-100" : "opacity-0"
                }`}
                style={{ clipPath: "polygon(0 0, 82% 0, 65% 100%, 0% 100%)" }}
              />
            </div>
          ))}

          {/* Semi-transparent blue gradient overlay matching logo colors */}
          <div
            className="absolute inset-0 z-10 pointer-events-none"
            style={{
              background:
                "linear-gradient(135deg, rgba(91,182,230,0.45) 0%, rgba(66,156,205,0.4) 50%, rgba(26,66,95,0.5) 100%)",
              clipPath: "polygon(0 0, 82% 0, 65% 100%, 0% 100%)"
            }}
          />
        </>
      )}
    </div>

    {/* 🌟 MAIN CARD */}
    <div
      className="relative z-20 w-full max-w-6xl lg:max-w-7xl
                 h-[88vh] sm:h-[82vh] lg:h-[85vh]
                 max-h-[640px] lg:max-h-none
                 mx-4 sm:mx-8 lg:mx-0

                 /* 📱 FULL TRANSPARENT GLASS */
                 bg-white/30 backdrop-blur-2xl border border-white/40 shadow-[0_20px_50px_rgba(0,40,120,0.15)]

                 /* 💻 DESKTOP ORIGINAL */
                 lg:bg-white lg:backdrop-blur-0 lg:shadow-xl lg:border-white/40

                 flex overflow-hidden rounded-[32px] lg:rounded-[56px]"
    >

      {/* LEFT PANEL (DESKTOP ONLY) */}
      <div
        className="relative hidden lg:flex w-1/2 h-full overflow-hidden"
        style={{
          clipPath:
            "path('M0,0 H88% Q100% 20% 96% 45% Q94% 60% 96% 75% Q100% 92% 88% 100% H0 Q8% 78% 0 50% Q8% 22% 0 0 Z')"
        }}
      >
        {/* Carousel Images */}
        {carouselSlides.map((slide, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-all duration-1000 ${
              index === currentImageIndex
                ? "opacity-100 scale-100"
                : "opacity-0 scale-110"
            }`}
          >
            <img src={slide.image} className="w-full h-full object-cover" alt={slide.title} />
          </div>
        ))}

        {/* Dark gradient overlay for text readability */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: "linear-gradient(to top, rgba(26,66,95,0.85) 0%, rgba(26,66,95,0.4) 40%, transparent 70%)"
          }}
        />

        {/* Logos - Top Left */}
        <div className="absolute top-8 left-8 z-20 flex items-center gap-5">
          <img
            src="/ietlogo.jpg"
            className="h-14 w-14 rounded-full shadow-lg bg-white p-2 object-cover hover:scale-110 transition-transform duration-300"
            alt="IET Logo"
          />
          <img
            src="/Logo_new_transparent.png"
            className="h-14 w-14 rounded-full shadow-lg bg-white p-1 object-contain hover:scale-110 transition-transform duration-300"
            alt="Acaera Logo"
          />
        </div>

        {/* Slide Content - Bottom */}
        <div className="absolute bottom-24 left-10 right-16 z-20">
          {carouselSlides.map((slide, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{
                opacity: index === currentImageIndex ? 1 : 0,
                y: index === currentImageIndex ? 0 : 20
              }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              className={`absolute bottom-0 left-0 right-0 ${index === currentImageIndex ? "pointer-events-auto" : "pointer-events-none"}`}
            >
              <h2 className="text-3xl font-bold text-white mb-3 drop-shadow-[0_2px_10px_rgba(0,0,0,0.5)]">
                {slide.title}
              </h2>
              <p className="text-white/90 text-base leading-relaxed max-w-sm drop-shadow-[0_1px_4px_rgba(0,0,0,0.4)]">
                {slide.subtitle}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Navigation Controls - Bottom Right */}
        <div className="absolute bottom-8 right-10 z-20 flex items-center gap-3">
          {/* Slide Indicators */}
          <div className="flex gap-2 mr-4">
            {carouselSlides.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentImageIndex(index)}
                className={`h-2 rounded-full transition-all duration-300 ${
                  index === currentImageIndex
                    ? "w-6 bg-white"
                    : "w-2 bg-white/40 hover:bg-white/60"
                }`}
              />
            ))}
          </div>

          {/* Arrow Buttons */}
          <button
            onClick={prevSlide}
            className="w-11 h-11 rounded-full bg-white/15 backdrop-blur-md border border-white/25 text-white hover:bg-[#429ccd] hover:border-[#429ccd] transition-all duration-300 flex items-center justify-center shadow-lg"
          >
            <ChevronLeft size={22} strokeWidth={2.5} />
          </button>
          <button
            onClick={nextSlide}
            className="w-11 h-11 rounded-full bg-white/15 backdrop-blur-md border border-white/25 text-white hover:bg-[#429ccd] hover:border-[#429ccd] transition-all duration-300 flex items-center justify-center shadow-lg"
          >
            <ChevronRight size={22} strokeWidth={2.5} />
          </button>
        </div>
      </div>

      {/* RIGHT PANEL */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-6 sm:px-10 lg:px-20 py-10 bg-transparent lg:bg-white">
        <div className="w-full max-w-md">

          <div className="flex justify-center mb-4">
            <img src="/Logo1.png" alt="Logo" className="h-24 sm:h-28 lg:h-32 object-contain" />
          </div>

          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-center" style={{ color: "#1a425f" }}>Welcome Back</h1>
          <p className="text-center text-black-700 mb-8">Continue your learning journey</p>

          <input type="email" placeholder="Email" value={formData.email} onChange={(e)=>setFormData({...formData,email:e.target.value})} className="w-full border border-gray-300 rounded-xl px-4 py-3 mb-4 focus:ring-2 outline-none" />

          <div className="relative mb-4">
            <input type={showPassword ? "text" : "password"} placeholder="Password" value={formData.password} onChange={(e)=>setFormData({...formData,password:e.target.value})} className="w-full border border-gray-300 rounded-xl px-4 py-3 outline-none" />
            <button type="button" onClick={()=>setShowPassword(!showPassword)} className="absolute right-4 top-3 text-gray-500">
              {showPassword ? <EyeOff size={18}/> : <Eye size={18}/>}
            </button>
          </div>

          <div className="text-right text-sm mb-5 cursor-pointer" style={{ color: "#429ccd" }}>Forgot password?</div>

          <div className="flex items-center gap-3 mb-5">
            <div className="flex-1 h-px bg-gray-200" />
            <span className="text-xs text-black-200">or</span>
            <div className="flex-1 h-px bg-gray-200" />
          </div>

          <button onClick={handleGoogleLogin} disabled={samlLoading} className="w-full flex items-center justify-center gap-3 border border-gray-300 py-3 rounded-xl hover:bg-gray-50 transition mb-5">
            <FcGoogle size={20}/> Continue with Google
          </button>

          <button
            onClick={handleSubmit}
            disabled={loading}
            className="w-full text-white py-3 rounded-full font-semibold shadow-lg transition hover:scale-[1.02]"
            style={{ background: "linear-gradient(90deg, #429ccd, #1a425f)" }}
          >
            {loading ? "Signing in..." : "Sign In"}
          </button>

        </div>
      </div>
    </div>
  </div>
);




};

export default Login;
